// Const enums (compatible with erasableSyntaxOnly)
export const UserRole = {
  ADMIN: "ADMIN",
  SELLER: "SELLER",
  HOST: "HOST",
  KIOSK: "KIOSK",
  CUSTOMER: "CUSTOMER",
  SALES_AGENT: "SALES_AGENT",
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** Mirrors backend `app.core.permissions.Permission` (JWT `permissions` claim). */
export const Permission = {
  PARTNERS_READ_ANY: "partners:read_any",
  HOSTS_READ_ANY: "hosts:read_any",
  ACTIVITIES_MANAGE_ANY: "activities:manage_any",
  ACTIVITIES_READ_ANY: "activities:read_any",
  ACTIVITIES_CONFIG_UPDATE: "activities:config:update",
  USERS_READ_SELF: "users:read_self",
  USERS_UPDATE_SELF: "users:update_self",
  USERS_READ_ANY: "users:read_any",
  USERS_UPDATE_ANY: "users:update_any",
  USERS_ACCOUNT_STATUS_UPDATE: "users:account_status:update",
  USERS_IMPERSONATE: "users:impersonate",
  ACTIVITIES_CREATE: "activities:create",
  ACTIVITIES_UPDATE: "activities:update",
  ACTIVITY_ITEMS_WRITE: "activity_items:write",
  ACTIVITIES_CATALOG_IMPORT: "activities:catalog:import",
  HOUSES_CREATE: "houses:create",
  HOUSES_READ: "houses:read",
  HOUSES_UPDATE: "houses:update",
  HOUSES_DELETE: "houses:delete",
  HOUSES_READ_ANY: "houses:read_any",
  HOUSES_UPDATE_ANY: "houses:update_any",
  HOUSES_PRESENCES_READ: "houses:presences:read",
  HOUSES_PRESENCES_IMPORT: "houses:presences:import",
  DEVICES_CREATE: "devices:create",
  DEVICES_READ: "devices:read",
  DEVICES_UPDATE: "devices:update",
  DEVICES_RESET_PASSWORD: "devices:reset_password",
  DEVICES_PRESETS_CREATE: "devices:presets:create",
  DEVICES_PRESETS_READ: "devices:presets:read",
  DEVICES_PRESETS_UPDATE: "devices:presets:update",
  DEVICES_HOUSE_CREATE: "devices:house:create",
  DEVICES_HOUSE_READ: "devices:house:read",
  DEVICES_HOUSE_RENAME: "devices:house:rename",
  DEVICES_HOUSE_UPDATE: "devices:house:update",
  DEVICES_HOUSE_RESET_PASSWORD: "devices:house:reset_password",
  READERS_PRESETS_CREATE: "readers:presets:create",
  READERS_PRESETS_READ: "readers:presets:read",
  READERS_PRESETS_UPDATE: "readers:presets:update",
  READERS_CREATE: "readers:create",
  READERS_READ: "readers:read",
  READERS_UPDATE: "readers:update",
  READERS_PAIR: "readers:pair",
  DEVICE_PRESENCES_READ: "device_presences:read",
  DEVICE_PRESENCES_CREATE: "device_presences:create",
  DEVICE_PRESENCES_UPDATE: "device_presences:update",
  DEVICE_PRESENCES_DELETE: "device_presences:delete",
  CHECKOUTS_READ: "checkouts:read",
  CHECKOUTS_WRITE: "checkouts:write",
  BOOKINGS_CREATE: "bookings:create",
  BOOKINGS_READ: "bookings:read",
  BOOKINGS_READ_ANY: "bookings:read_any",
  BOOKINGS_UPDATE: "bookings:update",
  BOOKINGS_UPDATE_ANY: "bookings:update_any",
  BOOKINGS_CANCEL: "bookings:cancel",
  BOOKINGS_CANCEL_ANY: "bookings:cancel_any",
  BOOKINGS_SELLER_RECEIPTS: "bookings:seller_receipts",
  BOOKINGS_AMEND_ANY: "bookings:amend_any",
  BOOKINGS_REFUND_ANY: "bookings:refund_any",
  HANDSHAKES_MESSAGE_SEND: "handshakes:message:send",
  HANDSHAKES_MESSAGE_SEND_ANY: "handshakes:message:send_any",
  HANDSHAKES_SESSIONS_READ_OWN: "handshakes:sessions:read_own",
  HANDSHAKES_SESSIONS_READ_ANY: "handshakes:sessions:read_any",
  HANDSHAKES_SESSION_CANCEL: "handshakes:session:cancel",
  HANDSHAKES_SESSION_CONFIRM: "handshakes:session:confirm",
  HANDSHAKES_SESSION_CONFIRM_ANY: "handshakes:session:confirm_any",
  HANDSHAKES_SESSION_REOPEN: "handshakes:session:reopen",
  CUSTOMER_SUPPORT_CHATS_READ_ANY: "customer_support_chats:read_any",
  CUSTOMER_SUPPORT_CHATS_MESSAGE: "customer_support_chats:message",
  CUSTOMER_SUPPORT_CHATS_CLOSE: "customer_support_chats:close",
  STORAGE_UPLOAD: "storage:upload",
  STORAGE_DELETE: "storage:delete",
  SUPPORT_TICKETS_CREATE: "support_tickets:create",
  SUPPORT_TICKETS_READ_OWN: "support_tickets:read_own",
  SUPPORT_TICKETS_MESSAGE_OWN: "support_tickets:message_own",
  SUPPORT_TICKETS_STATS_OWN: "support_tickets:stats_own",
  SUPPORT_TICKETS_READ_ANY: "support_tickets:read_any",
  SUPPORT_TICKETS_UPDATE_ANY: "support_tickets:update_any",
  SUPPORT_TICKETS_MESSAGE_ANY: "support_tickets:message_any",
  SUPPORT_TICKETS_STATS_ANY: "support_tickets:stats_any",
  SELLERS_CREATE: "sellers:create",
  SELLERS_READ_ANY: "sellers:read_any",
  SELLERS_UPDATE_ANY: "sellers:update_any",
  HOSTS_CREATE: "hosts:create",
  HOSTS_UPDATE_ANY: "hosts:update_any",
  REGIONS_LIST: "regions:list",
  STATS_READ: "stats:read",
  EXTERNAL_PAYMENTS_LIST: "external_payments:list",
  ECONOMIC_EVENTS_LIST: "economic_events:list",
  ECONOMIC_EVENTS_WRITE: "economic_events:write",
  SETTLEMENTS_LIST: "settlements:list",
  SETTLEMENTS_WRITE: "settlements:write",
  SETTLEMENT_DOCUMENTS_READ: "settlement_documents:read",
  NOTIFICATIONS_READ: "notifications:read",
  NOTIFICATIONS_UPDATE: "notifications:update",
  PROTOCOLS_READ: "protocols:read",
  PROTOCOLS_WRITE: "protocols:write",
  CAROUSEL_SLIDES_READ: "carousel_slides:read",
  CAROUSEL_SLIDES_WRITE: "carousel_slides:write",
  AGENT_VOICE_TRANSCRIPTS_READ: "agent_voice_transcripts:read",
  STAFF_CREATE: "staff:create",
  STAFF_PERMISSIONS_UPDATE: "staff:permissions:update",
  PLATFORM_HARD_DELETE: "platform:hard_delete",
  WALLET_READ: "wallet:read",
  SALES_AGENT_STATS_READ: "sales_agent:stats:read",
  SALES_AGENT_ASSIGNMENTS_READ: "sales_agent:assignments:read",
  SALES_AGENTS_READ_ANY: "sales_agents:read_any",
  SALES_AGENTS_MANAGE_ANY: "sales_agents:manage_any",
  AGENT_USE: "agent:use",
  CUSTOMER_BOOKINGS_READ: "customer:bookings:read",
  CUSTOMER_RECEIPT_MANAGE: "customer:receipt:manage",
  CUSTOMER_POINTS_READ: "customer:points:read",
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

/** UI / catalog category (mirrors backend `ActivityCategory`). */
export const ActivityCategory = {
  ATTRACTION: "ATTRACTION",
  GENERIC_SERVICE: "GENERIC_SERVICE",
  RESTAURANT: "RESTAURANT",
} as const;
export type ActivityCategory =
  (typeof ActivityCategory)[keyof typeof ActivityCategory];

export const ActivityPaymentModel = {
  UPFRONT: "UPFRONT",
  RECEIPT_BASED: "RECEIPT_BASED",
} as const;
export type ActivityPaymentModel =
  (typeof ActivityPaymentModel)[keyof typeof ActivityPaymentModel];

export type ReceiptAmountSource = "OCR" | "MANUAL";

export const PartnerReceiptUploadStatus = {
  UPLOADED: "uploaded",
  OCR_SUCCEEDED: "ocr_succeeded",
  OCR_FAILED: "ocr_failed",
  PARTNER_REJECTED: "partner_rejected",
  CONFIRMED: "confirmed",
} as const;
export type PartnerReceiptUploadStatus =
  (typeof PartnerReceiptUploadStatus)[keyof typeof PartnerReceiptUploadStatus];

export const ActivityAvailabilityMode = {
  SCHEDULED: "SCHEDULED",
  OPEN: "OPEN",
} as const;
export type ActivityAvailabilityMode =
  (typeof ActivityAvailabilityMode)[keyof typeof ActivityAvailabilityMode];

export const BookingQuantityMode = {
  GUESTS: "GUESTS",
  QUANTITY: "QUANTITY",
} as const;
export type BookingQuantityMode =
  (typeof BookingQuantityMode)[keyof typeof BookingQuantityMode];

/** Customer booking / handshake channel (matches backend `BookingSource`). */
export const BookingSource = {
  WHATSAPP: "WHATSAPP",
  SMS: "SMS",
  EMAIL: "EMAIL",
} as const;
export type BookingSource = (typeof BookingSource)[keyof typeof BookingSource];

export const ProfileType = {
  PERSON: "PERSON",
  COMPANY: "COMPANY",
} as const;
export type ProfileType = (typeof ProfileType)[keyof typeof ProfileType];

// Auth Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  // For HOST users, host_id equals user_id (hosts.user_id is the PK).
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token?: string | null;
  token_type: string;
  user: User;
}

export interface TokenPayload {
  sub: string; // user_id
  role: UserRole;
  email: string;
  permissions: Permission[];
  exp: number;
  impersonator_sub?: string;
}

export interface AuthState {
  user: TokenPayload | null;
  isAuthenticated: boolean;
}

// House Types
export interface House {
  id: string;
  host_user_id: string; // Reference to host's user_id (PK of hosts table)
  name: string;
  address?: string;
  coordinates?: { lat: number; lng: number };
  commission_rate: number; // Platform commission (0.0-1.0)
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HouseSummary {
  id: string;
  name: string;
}

export interface HouseWithDeviceCount extends House {
  device_count: number;
}

export interface HouseCreate {
  host_user_id: string; // Reference to host's user_id
  name: string;
  address?: string;
  coordinates?: { lat: number; lng: number } | null;
  /** Platform commission rate for this house (0.0–1.0). Required at creation (admin API). */
  commission_rate: number;
  /** Optional scoped sales agent. */
  sales_agent_user_id?: string | null;
  sales_agent_commission_rate?: number | null;
}

export interface HouseUpdate {
  name?: string;
  address?: string;
  coordinates?: { lat: number; lng: number } | null;
}

export interface HouseAdminUpdate extends HouseUpdate {
  commission_rate?: number; // 0.0-1.0
  is_active?: boolean;
}

export interface HouseListResponse {
  total: number;
  items: House[];
}

// Device Types
export const ReaderStatus = {
  CREATED: "created",
  NOT_PAIRED: "not_paired",
  PAIRED: "paired",
} as const;
export type ReaderStatus = (typeof ReaderStatus)[keyof typeof ReaderStatus];

/** SumUp GetReaderStatus device connectivity (`status`). */
export const ReaderDeviceConnectivity = {
  ONLINE: "ONLINE",
  OFFLINE: "OFFLINE",
} as const;
export type ReaderDeviceConnectivity =
  (typeof ReaderDeviceConnectivity)[keyof typeof ReaderDeviceConnectivity];

/** SumUp GetReaderStatus screen/device `state` values. */
export const ReaderDeviceState = {
  IDLE: "IDLE",
  SELECTING_TIP: "SELECTING_TIP",
  UPDATING_FIRMWARE: "UPDATING_FIRMWARE",
  WAITING_FOR_CARD: "WAITING_FOR_CARD",
  WAITING_FOR_PIN: "WAITING_FOR_PIN",
  WAITING_FOR_SIGNATURE: "WAITING_FOR_SIGNATURE",
} as const;
export type ReaderDeviceState =
  (typeof ReaderDeviceState)[keyof typeof ReaderDeviceState];

/** SumUp GetReaderStatus `connection_type` values. */
export const ReaderDeviceConnectionType = {
  WIFI: "Wi-Fi",
  BTLE: "btle",
  EDGE: "edge",
  GPRS: "gprs",
  LTE: "lte",
  UMTS: "umts",
  USB: "usb",
} as const;
export type ReaderDeviceConnectionType =
  (typeof ReaderDeviceConnectionType)[keyof typeof ReaderDeviceConnectionType];

/**
 * Persisted SumUp reader status payload (+ our `checked_at` poll timestamp).
 * Documented enums are preferred; `| string` keeps room for undocumented SumUp values.
 */
export interface DeviceStatusSnapshot {
  status: ReaderDeviceConnectivity | string;
  state?: ReaderDeviceState | string | null;
  battery_level?: number | null;
  battery_temperature?: number | null;
  connection_type?: ReaderDeviceConnectionType | string | null;
  firmware_version?: string | null;
  last_activity?: string | null;
  checked_at?: string | null;
}

/** Registered POS reader (SumUp Cloud API, mock, etc.) */
export interface PaymentReader {
  id: string;
  brand: string;
  model: string;
  serial_number: string;
  provider: string;
  flow: string;
  status: ReaderStatus;
  region_id: string;
  region_code: string;
  region_locked_at?: string | null;
  external_reader_id: string | null;
  provider_metadata: Record<string, unknown> | null;
  device_status_snapshot: DeviceStatusSnapshot | null;
  created_at: string;
  updated_at: string;
}

export interface ReaderInventory extends PaymentReader {
  /** Present iff the reader is linked to a device (backend invariant). */
  assigned_device: { id: string; display_name: string } | null;
}

export interface DeviceReaderSummary {
  id: string;
  serial_number: string;
}

export interface ReaderPreset {
  id: string;
  brand: string;
  model: string;
  provider: string;
  flow: string;
  created_at: string;
  updated_at: string;
}

export interface ReaderPresetCreate {
  brand: string;
  model: string;
  provider: string;
  flow: string;
}

export interface ReaderCreate {
  reader_preset_id: string;
  serial_number: string;
  region_id: string;
}

import type {
  DeviceLocationPayload,
  DeviceStatusPayload,
} from "@/lib/deviceStatus";

export type { DeviceLocationPayload, DeviceStatusPayload };

// Physical device types (tablet / kiosk hardware)
export interface Device {
  id: string;
  region_id: string | null;
  /** Host payment region when assigned to a house; null only for unassigned inventory. */
  region_code: RegionCode | null;
  display_name: string;
  kiosk_code: string;
  serial_number: string;
  google_account: string;
  software_id?: string | null;
  brand?: string | null;
  model?: string | null;
  android_version?: string | null;
  display_resolution?: string | null;
  display_inches?: number | null;
  house_id: string | null;
  house_sort_order?: number;
  house?: HouseSummary | null;
  /** True = credentials revoked (login blocked). Distinct from is_shelved. */
  is_revoked: boolean;
  /** Informational: tablet set aside / not in live deployment. No auth side effects. */
  is_shelved: boolean;
  notes: string | null;
  last_login_at: string | null;
  /** Last authenticated kiosk API interaction (excludes telemetry/refresh). */
  last_interaction_at: string | null;
  created_at: string;
  updated_at: string;
  current_presence?: DevicePresence | null;
  /** Number of future guest stays queued on this device (house device lists). */
  upcoming_presence_count?: number;
  /** Lightweight linked reader summary for device management views. */
  reader?: DeviceReaderSummary | null;
  /** Last tablet telemetry POST (ISO). */
  last_device_report_at?: string | null;
  /** FreeKiosk /status JSON from the tablet (minimal portal shape). */
  device_status_payload?: DeviceStatusPayload | null;
  /** GPS / location JSON from the tablet app (minimal portal shape). */
  device_location_payload?: DeviceLocationPayload | null;
  /** FreeKiosk app unlock code (derived server-side from serial). */
  freekiosk_unlock_code?: string | null;
}

/** Device from house-scoped routes — host payment region is always present. */
export type HouseDevice = Device & { region_code: RegionCode };

export interface DeviceCreate {
  display_name?: string;
  serial_number: string;
  google_account: string;
  device_preset_id?: string;
  brand?: string;
  model?: string;
  android_version?: string;
  display_resolution?: string;
  display_inches?: number;
  password?: string;
}

export interface DeviceCreateResponse extends Device {
  password: string;
}

export interface DeviceUpdate {
  display_name?: string;
  google_account?: string;
  region_id?: string;
  is_revoked?: boolean;
  is_shelved?: boolean;
  notes?: string | null;
  brand?: string;
  model?: string;
  android_version?: string;
  display_resolution?: string;
  display_inches?: number;
  reader_id?: string | null;
}

export interface DevicePreset {
  id: string;
  brand: string;
  model?: string | null;
  android_version: string;
  display_resolution: string;
  display_inches: number;
  created_at: string;
  updated_at: string;
}

export interface DevicePresetCreate {
  brand: string;
  model?: string;
  android_version: string;
  display_resolution: string;
  display_inches: number;
}

// Device presence types (guest occupancy per tablet)
export interface MarketingConsent {
  id: string;
  source: "CHECKIN" | "PAYMENT";
  device_presence_id: string | null;
  checkout_id: string | null;
  first_name: string;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  nationality: string | null;
  num_people: number | null;
  has_children: boolean | null;
  consent_marketing: boolean;
  consent_profiling: boolean;
  consent_third_party: boolean;
  consented_at: string;
  created_at: string;
}

export interface DevicePresence {
  id: string;
  device_id: string;
  guest_name: string | null;
  nationality: string;
  arrival_date: string; // YYYY-MM-DD
  arrival_time: string; // HH:MM or HH:MM:SS
  departure_date: string; // YYYY-MM-DD
  departure_time: string; // HH:MM or HH:MM:SS
  num_people: number;
  has_children: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  marketing_consent?: MarketingConsent | null;
}

export interface DevicePresenceCreate {
  guest_name: string;
  last_name?: string;
  nationality: string;
  /** When set, schedules a future stay; omit for immediate check-in (now). */
  arrival_date?: string;
  arrival_time?: string; // HH:MM
  departure_date: string;
  departure_time?: string; // HH:MM
  num_people: number;
  has_children: boolean;
  notes?: string;
  email?: string;
  phone?: string;
  consent_marketing?: boolean;
  consent_profiling?: boolean;
  consent_third_party?: boolean;
}

export interface DevicePresenceUpdate {
  guest_name: string;
  nationality: string;
  arrival_date?: string;
  arrival_time?: string;
  departure_date?: string;
  departure_time?: string;
  num_people?: number;
  has_children?: boolean;
  notes?: string;
}

export interface HouseCurrentPresenceItem extends DevicePresence {
  device_display_name: string;
  is_current: boolean;
}

export interface HousePresenceDailyBucket {
  date: string;
  device_presence_seconds: number;
  interval_capacity_seconds: number;
  occupancy_percentage: number;
}

export const RegionCode = {
  IT: "IT",
  ES: "ES",
} as const;
export type RegionCode = (typeof RegionCode)[keyof typeof RegionCode];

/** Payment/processing region (public row from GET /regions). */
export interface Region {
  id: string;
  code: RegionCode;
  name: string;
  currency: string;
  is_active: boolean;
}

// Seller account types (admin CRUD)
export interface SellerAccount {
  user_id: string; // Primary key (references users.id)
  name: string;
  // Note: The type (ATTRACTION/SERVICE/RESTAURANT) is determined by the Activity,
  // not by the seller. A seller can have activities of different types.
  // Legal contact and billing details live in legal-info APIs, not on the Seller record itself.
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Shared create payload for seller / host / sales-agent staff accounts. */
export interface StaffAccountCreate {
  name: string;
  email: string;
  region_id: string;
  /** E.164 phone stored on the legal profile at create. */
  contact_phone: string;
}

export type SellerAccountCreate = StaffAccountCreate;

// Host Types
export interface Host {
  user_id: string; // Primary key (references users.id)
  name: string;
  account_email: string;
  /** From user legal info (contact_phone). */
  contact_phone?: string | null;
  created_at: string;
  updated_at: string;
}

export type HostCreate = StaffAccountCreate;

export interface SellerProfile {
  user_id: string;
  role: UserRole;
  email: string;
  is_active: boolean;
  name: string;
  /** Primary contact phone from legal info (hosts); partners omit. */
  contact_phone?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SellerProfileUpdate {
  name?: string;
  is_active?: boolean;
}

export interface SellerWallet extends SellerProfile {
  /** Integer cents. */
  wallet_balance: number;
}

export interface SalesAgentRead {
  user_id: string;
  email: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SalesAgentListAdminItem extends SalesAgentRead {
  assigned_scopes_count: number;
  /** Integer cents. */
  wallet_balance: number;
  contact_phone?: string | null;
}

export type SalesAgentCreatePayload = StaffAccountCreate;

export interface SalesAgentDashboard {
  wallet_balance_cents: number;
  total_credits_cents: number;
  assigned_scopes_count: number;
}

/** One activity or house where the logged-in sales agent earns commission. */
export interface SalesAgentMeAssociationItem {
  scope: "activity" | "house";
  scope_id: string;
  name: string;
  seller_user_id: string;
  seller_name: string;
  commission_rate: number;
}

export interface SalesAgentActivityAssignmentRow {
  activity_id: string;
  activity_name: string;
  owner_user_id: string;
  owner_name: string;
  sales_agent_user_id: string;
  commission_rate: number;
}

/** Admin: sales agent assignment for a single activity (activity detail shortcut). */
export interface ActivitySalesAgentAssignmentDetail {
  activity_id: string;
  sales_agent_user_id: string;
  sales_agent_email: string;
  sales_agent_is_active: boolean;
  commission_rate: number;
  assigned_at: string;
}

export interface SalesAgentHouseAssignmentRow {
  house_id: string;
  house_name: string;
  host_user_id: string;
  host_name: string;
  sales_agent_user_id: string;
  commission_rate: number;
}

export interface SalesAgentScopedAssignmentsResponse {
  activities: SalesAgentActivityAssignmentRow[];
  houses: SalesAgentHouseAssignmentRow[];
}

// Activity Types
export interface MediaItem {
  url: string;
  type: "image" | "video";
  order: number;
  thumbnail_url?: string;
}

import type { SupportedLocale } from "@/lib/locales";

export type { SupportedLocale };

export interface ActivityBase {
  name_translations: Translations;
  description_translations?: Translations | null;
  // Address components
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  media?: MediaItem[];
  // Restaurant-specific fields
  cuisine_types?: string[];
  /** Whole euros (not cents) — matches backend Activity field. */
  avg_price_per_person?: number;
  service_categories?: ServiceCategory[];
}

export interface Activity extends ActivityBase {
  id: string;
  user_id: string; // Canonical owner id (users.id)
  seller_id: string; // Owner id from API (same as user_id for seller-owned activities)
  category: ActivityCategory;
  payment_model: ActivityPaymentModel;
  availability_mode: ActivityAvailabilityMode;
  booking_quantity_mode: BookingQuantityMode;
  is_active: boolean;
  is_visible: boolean;
  // Handshake flag: if true, bookings require partner confirmation
  requires_handshake?: boolean;
  /** Minutes before slot start when bookings close (0 — default from API/parser). */
  booking_cutoff_minutes: number;
  commission_rate: number; // Platform commission (0.0-1.0)
  region_id: string;
  region_code: string;
  coordinates?: { lat: number; lng: number };
  created_at: string;
  updated_at: string;
}

export interface ActivityCreate extends ActivityBase {
  category: ActivityCategory;
  payment_model: ActivityPaymentModel;
  availability_mode?: ActivityAvailabilityMode;
  booking_quantity_mode: BookingQuantityMode;
  requires_handshake?: boolean;
  booking_cutoff_minutes?: number;
  /** Payment region (immutable after creation; must match owner legal profile). */
  region_id: string;
  /** Seller or host user_id that owns this activity (required; admin-only create). */
  owner_user_id: string;
  /** Platform commission rate for this activity (0.0–1.0). Required at creation. */
  commission_rate: number;
  /** Optional scoped sales agent. */
  sales_agent_user_id?: string | null;
  /** Sales agent commission share of step-1 pool (0–1) when sales_agent_user_id is set. */
  sales_agent_commission_rate?: number | null;
  coordinates?: { lat: number; lng: number } | null;
}

export interface ActivityUpdate {
  name_translations?: Translations;
  category?: ActivityCategory;
  payment_model?: ActivityPaymentModel;
  availability_mode?: ActivityAvailabilityMode;
  booking_quantity_mode?: BookingQuantityMode;
  description_translations?: Translations | null;
  // Address components
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  coordinates?: { lat: number; lng: number } | null;
  media?: MediaItem[];
  is_active?: boolean;
  is_visible?: boolean;
  // Handshake flag: if true, bookings require partner confirmation
  requires_handshake?: boolean;
  booking_cutoff_minutes?: number;
  // Restaurant-specific fields
  cuisine_types?: string[];
  /** Whole euros (not cents) — matches backend Activity field. */
  avg_price_per_person?: number;
  service_categories?: ServiceCategory[];
  commission_rate?: number; // 0.0-1.0
}

// Wallet Types (unified for all users)
export type WalletMovementKind =
  | "income"
  | "expense"
  | "settlement_transfer"
  | "refund";

export type EconomicEventType =
  | "CHECKOUT_CAPTURED"
  | "BOOKING_PAYMENT_CAPTURED"
  | "SELLER_FEE_FROM_RECEIPT"
  | "SELLER_FEE_FROM_RECEIPT_REVERSAL"
  | "REFUND_REVERSAL"
  | "SELLER_BANK_TRANSFER_SENT"
  | "SELLER_BANK_TRANSFER_RECEIVED";

export interface WalletTransaction {
  id: string;
  // Owner - the user who owns this transaction
  user_id?: string;
  wallet_account_id?: string;
  ledger_entry_id?: string;
  economic_event_id?: string;
  // Booking references
  booking_id?: string;
  activity_id?: string;
  // Transaction data
  type: "CREDIT" | "DEBIT";
  /** Integer cents. */
  amount: number;
  currency?: string;
  description: string;
  metadata_json?: Record<string, unknown> | null;
  movement_key?: string;
  movement_kind?: WalletMovementKind;
  entry_role?: string;
  economic_event_type: EconomicEventType;
  display_context?: Record<string, string>;
  /** Integer cents. */
  balance_before?: number;
  /** Integer cents. */
  balance_after?: number;
  idempotency_key?: string;
  created_at: string;
  // Optional enriched fields (may be populated by frontend or future backend)
  activity_name?: string;
  service_name?: string;
  customer_name?: string;
}

/** Shared list envelope used by several admin/audit endpoints. */
export type Paginated<T> = {
  items: T[];
  total: number;
  skip: number;
  limit: number;
};

/** Shared total+items envelope (no skip/limit echo). */
export type ListResponse<T> = {
  total: number;
  items: T[];
};

export type WalletTransactionsResponse = ListResponse<WalletTransaction>;

// API Response Types
export interface PaginationParams {
  skip?: number;
  limit?: number;
}

// Translation Types
export type CompleteTranslations = Record<SupportedLocale, string>;
export type Translations = Record<SupportedLocale, string | null>;

export interface ServiceCategory {
  id: string;
  name_translations: Translations;
  display_order: number;
}

// Menu Item Types (for restaurants)
export const MenuCategory = {
  APPETIZER: "APPETIZER",
  FIRST_COURSE: "FIRST_COURSE",
  SECOND_COURSE: "SECOND_COURSE",
  SIDE_DISH: "SIDE_DISH",
  SALAD: "SALAD",
  PIZZA: "PIZZA",
  SOUP: "SOUP",
  DESSERT: "DESSERT",
  FRUIT: "FRUIT",
  CHEESE: "CHEESE",
  BEVERAGE: "BEVERAGE",
  WINE: "WINE",
  COCKTAIL: "COCKTAIL",
  COFFEE: "COFFEE",
  KIDS_MENU: "KIDS_MENU",
  SPECIAL: "SPECIAL",
} as const;
export type MenuCategory = (typeof MenuCategory)[keyof typeof MenuCategory];

/** Unified catalog line (menu row and/or upfront SKU). */
export interface ActivityItem {
  id: string;
  activity_id: string;
  menu_category: MenuCategory | null;
  service_category_id: string | null;
  name_translations: Translations;
  description_translations: Translations | null;
  price: number; // in cents
  undiscounted_price?: number | null;
  image_url?: string | null;
  commission_rate: number;
  is_visible: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ActivityItemCreate {
  menu_category?: MenuCategory | null;
  service_category_id?: string | null;
  name_translations: Translations;
  description_translations?: Translations | null;
  price: number;
  undiscounted_price?: number | null;
  commission_rate: number;
}

export interface ActivityItemUpdate {
  menu_category?: MenuCategory | null;
  service_category_id?: string | null;
  name_translations?: Translations;
  description_translations?: Translations | null;
  price?: number;
  undiscounted_price?: number | null;
  commission_rate?: number;
  is_visible?: boolean;
  display_order?: number;
}

// ========== Protocol Types ==========

export const ProtocolStatus = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  OBSOLETE: "OBSOLETE",
} as const;
export type ProtocolStatus =
  (typeof ProtocolStatus)[keyof typeof ProtocolStatus];

export const ProtocolVersionBump = {
  MINOR: "MINOR",
  MAJOR: "MAJOR",
} as const;
export type ProtocolVersionBump =
  (typeof ProtocolVersionBump)[keyof typeof ProtocolVersionBump];

// ========== Booking Types ==========

export const BookingStatus = {
  CREATED: "CREATED",
  PENDING_HANDSHAKE: "PENDING_HANDSHAKE",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  COMPLETED: "COMPLETED",
  CANCELED: "CANCELED",
  ABANDONED: "ABANDONED",
} as const;
export type BookingStatus = (typeof BookingStatus)[keyof typeof BookingStatus];

export type BookingLifecycleStatus = BookingStatus;

export const CancellationSource = {
  CUSTOMER: "CUSTOMER",
  PARTNER: "PARTNER",
  SYSTEM: "SYSTEM",
} as const;
export type CancellationSource =
  (typeof CancellationSource)[keyof typeof CancellationSource];

export interface BookingLineItem {
  id: string;
  booking_id: string;
  activity_item_id: string;
  quantity: number;
  /** List unit price before seller markdown (undiscounted or catalog). */
  list_unit_price_cents: number;
  /** List total before seller markdown. */
  list_total_price_cents: number;
  /** Seller-funded markdown (list − catalog/original). */
  seller_discount_cents: number;
  /** Catalog unit price before platform promo (settlement base). */
  original_unit_price_cents: number;
  /** Catalog total before platform promo (settlement base). */
  original_total_price_cents: number;
  unit_price_cents: number;
  total_price_cents: number;
  /** Platform / Flitrip promo discount on this line. */
  discount_cents: number;
  commission_rate?: number;
  commission_amount_cents?: number;
  activity_item_name?: string | null;
}

export interface BookingCardLineItem {
  id: string;
  quantity: number;
  activity_item_name?: string | null;
}

/** GET /bookings list payload; full detail remains on GET /bookings/{id}. */
export interface BookingCard {
  id: string;
  activity_id: string;
  status: BookingLifecycleStatus;
  cancellation_source: CancellationSource | null;
  quantity: number;
  people_count: number | null;
  time_slot: string;
  visit_at: string;
  activity_region_code: RegionCode;
  activity_name: string;
  activity_payment_model: ActivityPaymentModel;
  activity_item_name: string | null;
  contact_first_name: string;
  contact_last_name: string;
  total_price_cents: number | null;
  /** Customer-paid checkout total after promo/points; null when no checkout. */
  amount_paid_cents: number | null;
  /** Loyalty points discount on the linked checkout; null when no checkout. */
  points_discount_cents: number | null;
  items: BookingCardLineItem[];
  handshake_session_id: string | null;
  handshake_message_count: number;
  handshake_status: HandshakeSessionStatus | null;
  customer_notes: string | null;
  seller_notes: string | null;
  created_at: string;
  partner_receipt_total_cents: number | null;
  partner_receipt_url: string | null;
  partner_receipt_manual_amount_required: boolean;
  partner_receipt_latest_upload_path: string | null;
}

/** Partner to-do bucket (backend `BookingActionCategory`). */
export type BookingActionCategory = "CONFIRMATION" | "RECEIPT" | "COMPLETION";

export type BookingListOrder = "CREATED_DESC" | "VISIT_ASC";

/**
 * Filters shared by `GET /bookings/page|facets|attention|days`. Array params are
 * repeated (`?status=A&status=B`) and match any of their values.
 */
export interface BookingListFilters {
  seller_user_id?: string;
  status?: BookingLifecycleStatus[];
  activity_id?: string[];
  /** Activity-local visit dates (`YYYY-MM-DD`, inclusive); always sent together. */
  visit_date_from?: string;
  visit_date_to?: string;
  action?: BookingActionCategory[];
}

export interface BookingPageParams extends BookingListFilters, PaginationParams {
  order?: BookingListOrder;
}

export interface BookingCardPage {
  items: BookingCard[];
  total: number;
}

/** Each facet ignores its own filter. */
export interface BookingFacetCounts {
  by_status: Record<BookingLifecycleStatus, number>;
  by_activity: Record<string, number>;
}

export interface BookingAttentionSummary {
  by_action: Record<BookingActionCategory, number>;
  /** Bookings needing at least one action. */
  total: number;
  /** Party size of open bookings visiting today (activity-local). */
  guests_today: number;
}

export interface BookingDaySummary {
  date: string;
  bookings: number;
  guests: number;
}

export interface UnifiedBooking {
  id: string;
  activity_id: string;
  items: BookingLineItem[];
  device_id: string;
  status: BookingLifecycleStatus;
  cancellation_source?: CancellationSource | null;
  quantity: number;
  people_count?: number;
  time_slot: string;
  receipt_total_cents?: number;
  total_price_cents?: number;
  /** Customer-paid checkout total after promo/points; null when no checkout. */
  amount_paid_cents?: number | null;
  /** Loyalty points discount on the linked checkout; null when no checkout. */
  points_discount_cents?: number | null;
  visit_at: string;
  activity_region_code: RegionCode;
  customer_notes?: string;
  seller_notes?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_first_name: string;
  contact_last_name: string;
  host_user_id?: string;
  house_id?: string | null;
  house_name?: string | null;
  booking_source: BookingSource;
  language: SupportedLocale;
  created_at: string;
  updated_at: string;
  // Optional display fields
  activity_item_name?: string;
  activity_name: string;
  device_name?: string;
  partner_receipt_url?: string;
  partner_receipt_total_cents?: number;
  partner_receipt_failed_validation_attempts: number;
  partner_receipt_max_validation_attempts: number;
  partner_receipt_remaining_validation_attempts: number;
  partner_receipt_manual_amount_required: boolean;
  partner_receipt_amount_source?: ReceiptAmountSource | null;
  partner_receipt_latest_upload_path?: string;
  partner_receipt_latest_upload_url?: string;
  partner_receipt_upload_history?: PartnerReceiptUpload[];
  customer_receipt_url?: string;
  customer_receipt_total_cents?: number;
  handshake_session_id: string | null;
  handshake_message_count: number;
  handshake_status: HandshakeSessionStatus | null;
  external_payment_id?: string | null;
  activity_payment_model: ActivityPaymentModel;
}

export type Booking = UnifiedBooking;

export interface CustomerReceiptUploadResponse {
  path: string;
  file_url: string;
}

export interface CustomerReceiptConfirmResponse {
  receipt_id: string;
  booking_id: string;
  awarded_points: number;
  total_points: number;
  already_processed: boolean;
  ocr_success: boolean;
  extracted_total_cents: number;
  extracted_total_display: string;
}

export interface PartnerReceiptUpload {
  storage_path: string;
  file_url: string;
  status: PartnerReceiptUploadStatus;
  uploaded_at?: string | null;
  validated_at?: string | null;
  confirmed_at?: string | null;
  ocr_total_cents?: number | null;
  confirmed_total_cents?: number | null;
}

export interface ReceiptAnalyzeResponse {
  ocr_success: boolean;
  extracted_total_cents?: number;
  extracted_total_display?: string;
  error_message?: string;
  public_url: string;
  fraud_warning?: string;
  failed_validation_attempts?: number;
  max_validation_attempts?: number;
  remaining_validation_attempts?: number;
  manual_amount_required?: boolean;
}

/** Customer capture = upload + OCR in one step. */
export interface ReceiptCaptureResponse extends ReceiptAnalyzeResponse {
  storage_path: string;
  file_url: string;
}

export interface CustomerPointsTransaction {
  id: string;
  points: number;
  /** Mirrors backend ``CustomerPointReason``. */
  reason:
    | "RECEIPT_UPLOAD"
    | "BOOKING_COMPLETED"
    | "SIGNUP_BONUS"
    | "POINTS_RESERVE"
    | "POINTS_RESTORE"
    | "MANUAL_CREDIT";
  booking_id?: string;
  created_at: string;
}

export interface CustomerPointsSummary {
  points_balance: number;
  points_for_100_cents: number;
  balance_value_cents: number;
  transactions: CustomerPointsTransaction[];
}

// ========== Schedule Types ==========

export const MealType = {
  BREAKFAST: "breakfast",
  LUNCH: "lunch",
  DINNER: "dinner",
  APERITIF: "aperitif",
} as const;
export type MealType = (typeof MealType)[keyof typeof MealType];

export const Weekday = {
  MONDAY: "monday",
  TUESDAY: "tuesday",
  WEDNESDAY: "wednesday",
  THURSDAY: "thursday",
  FRIDAY: "friday",
  SATURDAY: "saturday",
  SUNDAY: "sunday",
} as const;
export type Weekday = (typeof Weekday)[keyof typeof Weekday];

export const ALL_WEEKDAYS = [
  Weekday.MONDAY,
  Weekday.TUESDAY,
  Weekday.WEDNESDAY,
  Weekday.THURSDAY,
  Weekday.FRIDAY,
  Weekday.SATURDAY,
  Weekday.SUNDAY,
] as const satisfies readonly Weekday[];

export interface TimeSlot {
  start_time: string; // "HH:MM"
  end_time: string | null;
  max_capacity?: number | null;
  meal_type?: MealType | null;
}

export type WeeklySchedule = Record<(typeof ALL_WEEKDAYS)[number], TimeSlot[]>;

export interface ScheduleOverride {
  id: string;
  name: string;
  date_from: string; // "YYYY-MM-DD"
  date_to: string; // "YYYY-MM-DD"
  is_closed: boolean;
  slots: TimeSlot[];
}

export interface ScheduleOverrideCreate {
  name: string;
  date_from: string;
  date_to: string;
  is_closed: boolean;
  slots: TimeSlot[];
}

export interface ScheduleResponse extends WeeklySchedule {
  overrides: ScheduleOverride[];
}

export interface SlotAvailability {
  start_time: string;
  end_time: string | null;
  meal_type?: MealType | null;
  max_capacity?: number | null;
  /** null when the partner only reports remaining seats (booked count unknown). */
  booked_count: number | null;
  available_spots?: number | null;
  is_available: boolean;
}

export interface DayAvailability {
  date: string;
  is_closed: boolean;
  is_override: boolean;
  override_name?: string | null;
  slots: SlotAvailability[];
}

export interface AvailabilityResponse {
  activity_id: string;
  activity_name: string;
  days: DayAvailability[];
}

// ========== Handshake Types ==========

export const HandshakeSessionStatus = {
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  EXPIRED: "EXPIRED",
  CANCELLED: "CANCELLED",
} as const;
export type HandshakeSessionStatus =
  (typeof HandshakeSessionStatus)[keyof typeof HandshakeSessionStatus];

export const HandshakeMessageStatus = {
  DELIVERED: "DELIVERED",
  BLOCKED: "BLOCKED",
} as const;
export type HandshakeMessageStatus =
  (typeof HandshakeMessageStatus)[keyof typeof HandshakeMessageStatus];

export const HandshakeSenderType = {
  CUSTOMER: "CUSTOMER",
  SELLER: "SELLER",
  SYSTEM: "SYSTEM",
} as const;
export type HandshakeSenderType =
  (typeof HandshakeSenderType)[keyof typeof HandshakeSenderType];

/** Matches backend ``SystemMessageType`` (handshake system gpt_analysis.type). */
export const SystemMessageType = {
  WELCOME: "system_welcome",
  CANCELLATION: "system_cancellation",
  EXPIRATION: "system_expiration",
  AGREEMENT_CONFIRMED: "agreement_confirmed",
  REOPENED: "system_reopened",
  OUTBOUND_CUSTOMER: "outbound_customer",
} as const;
export type SystemMessageType =
  (typeof SystemMessageType)[keyof typeof SystemMessageType];

/** Booking snapshot in `handshake_sessions.context` (JSONB). Matches backend `HandshakeContext`. */
export interface HandshakeCustomerContact {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export type HandshakeTimeSelectionMode = "SCHEDULED_SLOT" | "CUSTOM_TIME";

export type ConversationMessageChannel =
  | "WHATSAPP"
  | "SMS"
  | "EMAIL"
  | "INTERNAL"
  | "PORTAL";

export type MessageDeliveryStatus = "SUBMITTED" | "DELIVERED" | "FAILED";

export interface HandshakeContext {
  quantity: number;
  booking_quantity_mode: BookingQuantityMode;
  availability_mode: ActivityAvailabilityMode;
  customer_notes?: string | null;
  initial_email_message_id?: string | null;
  visit_at: string;
  time_slot: string;
  activity_region_code: RegionCode;
}

export interface HandshakeSession {
  id: string;
  booking_id: string;
  seller_user_id: string;
  customer_channel: BookingSource;
  customer_language: SupportedLocale;
  status: HandshakeSessionStatus;
  context: HandshakeContext;
  created_at: string;
  updated_at: string;
  closed_at?: string;
}

/** Partner/admin list row — includes computed display fields. */
export interface HandshakeSessionListItem extends HandshakeSession {
  seller_name: string;
  /** Partner-language booking title (computed from booking) */
  title: string;
}

export interface HandshakeMessagePublic {
  id: string;
  conversation_id: string;
  sender_type: HandshakeSenderType;
  sent_by_admin?: boolean;
  content: string;
  message_status: HandshakeMessageStatus;
  block_reason?: string;
  /** Always present; null when sender is not SYSTEM or legacy rows lack type. */
  system_type: SystemMessageType | null;
  created_at: string;
}

export interface HandshakeMessageFull {
  id: string;
  conversation_id: string;
  sender_type: HandshakeSenderType;
  sent_by_admin?: boolean;
  content: string;
  message_status: HandshakeMessageStatus;
  block_reason?: string;
  gpt_analysis: Record<string, unknown>;
  agreement_detected: boolean;
  channel: ConversationMessageChannel;
  delivery_status: MessageDeliveryStatus | null;
  delivered_at: string | null;
  created_at: string;
}

export interface HandshakeSessionPublicDetail {
  id: string;
  booking_id: string;
  status: HandshakeSessionStatus;
  context: HandshakeContext;
  customer_channel: BookingSource;
  customer_language: SupportedLocale;
  customer: HandshakeCustomerContact;
  created_at: string;
  updated_at: string;
  messages: HandshakeMessagePublic[];
}

export interface HandshakeSessionDetail extends HandshakeSession {
  customer: HandshakeCustomerContact;
  messages: HandshakeMessageFull[];
}

export interface HandshakeSessionListResponse {
  total: number;
  items: HandshakeSessionListItem[];
}

export interface ProcessMessageResult {
  message_id: string;
  status: HandshakeMessageStatus;
  blocked: boolean;
  block_reason?: string;
  response_to_sender?: string;
}

export interface AddressSuggestion {
  id: string;
  label: string;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  coordinates?: { lat: number; lng: number } | null;
}

export type AddressValidationResult =
  | { valid: true }
  | { valid: false; reason: string };

export type AddressResolveResult =
  | { coordinates: { lat: number; lng: number } }
  | { coordinates: null; reason: string };

export type AddressValidationStatus =
  | "idle"
  | "incomplete"
  | "checking"
  | "valid"
  | "invalid";

export type AddressValidationState =
  | { status: Exclude<AddressValidationStatus, "invalid"> }
  | { status: "invalid"; reason: string };
